const asRecord = (value: unknown): Record<string, unknown> | null =>
  value != null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const dedupeErrors = (errors: string[]) =>
  [...new Set(errors.map(e => e.trim()).filter(Boolean))];

const FIELD_LABELS: Record<string, string> = {
  'EInvoice.SellerDtls.Ph': 'Seller phone number',
  'SellerDtls.Ph': 'Seller phone number',
  'EInvoice.SellerDtls.Pin': 'Seller PIN code',
  'SellerDtls.Pin': 'Seller PIN code',
  'EInvoice.SellerDtls.Gstin': 'Seller GSTIN',
  'SellerDtls.Gstin': 'Seller GSTIN',
  'EInvoice.BuyerDtls.Pin': 'Buyer PIN code',
  'BuyerDtls.Pin': 'Buyer PIN code',
  'EInvoice.BuyerDtls.Gstin': 'Buyer GSTIN',
  'BuyerDtls.Gstin': 'Buyer GSTIN',
  'EInvoice.BuyerDtls.Stcd': 'Buyer state code',
  'BuyerDtls.Stcd': 'Buyer state code',
  'EInvoice.DocDtls.No': 'Invoice number',
  'DocDtls.No': 'Invoice number',
  'buyerDetails.gstin': 'Buyer GSTIN',
  'sellerDetails.gstin': 'Seller GSTIN',
};

const resolveFieldLabel = (fieldPath: string): string => {
  const normalized = fieldPath.trim();
  if (FIELD_LABELS[normalized]) return FIELD_LABELS[normalized];

  for (const [key, label] of Object.entries(FIELD_LABELS)) {
    if (normalized.includes(key)) return label;
  }

  const lower = normalized.toLowerCase();
  if (lower.includes('gstin')) {
    if (lower.includes('buyer')) return 'Buyer GSTIN';
    if (lower.includes('seller')) return 'Seller GSTIN';
    return 'GSTIN';
  }
  if (lower.includes('pin')) {
    if (lower.includes('buyer')) return 'Buyer PIN code';
    if (lower.includes('seller')) return 'Seller PIN code';
    return 'PIN code';
  }

  const lastDot = normalized.lastIndexOf('.');
  const segment = lastDot >= 0 ? normalized.slice(lastDot + 1) : normalized;
  return segment.replace(/_/g, ' ');
};

/** Convert raw ClearTax / NIC error text into a user-facing line. */
export const formatClearTaxErrorMessage = (rawMessage: string, path?: string | null): string => {
  const trimmed = rawMessage.trim();
  const splitIndex = trimmed.indexOf(' : ');
  if (splitIndex > 0) {
    const fieldPath = trimmed.slice(0, splitIndex).trim();
    const description = trimmed.slice(splitIndex + 3).trim();
    return `${resolveFieldLabel(fieldPath)}: ${description}`;
  }
  if (path != null && String(path).trim()) {
    return `${resolveFieldLabel(String(path))}: ${trimmed}`;
  }
  return trimmed;
};

/** Read structured validation errors from bbr-api ExceptionResponse. */
export const parseApiExceptionErrors = (data: unknown): string[] => {
  const body = asRecord(data);
  if (!body) return [];

  const fromErrors = asArray(body.errors)
    .map(item => asRecord(item)?.defaultMessage)
    .filter(msg => msg != null && String(msg).trim())
    .map(msg => String(msg));

  return dedupeErrors(fromErrors);
};

const pushDetail = (errors: string[], detail: unknown) => {
  const row = asRecord(detail);
  if (!row) return;
  const message =
    row.error_message ?? row.ErrorMessage ?? row.errorMessage ?? row.message ?? row.desc ?? row.description;
  const path = row.Path ?? row.path ?? row.property_name ?? row.PropertyName ?? row.field;

  if (message != null && String(message).trim()) {
    errors.push(formatClearTaxErrorMessage(String(message), path != null ? String(path) : null));
  }
};

const collectErrorDetails = (node: Record<string, unknown> | null, errors: string[]) => {
  if (!node) return;
  asArray(node.ErrorDetails ?? node.errorDetails ?? node.error_details).forEach(d => pushDetail(errors, d));

  const errResp = asRecord(node.error_response ?? node.errorResponse);
  if (errResp) {
    asArray(errResp.ErrorDetails ?? errResp.errorDetails).forEach(d => pushDetail(errors, d));
    pushDetail(errors, errResp);
  }
};

const parseGovtResponse = (govt: Record<string, unknown>, errors: string[]) => {
  const success = String(govt.Success ?? govt.success ?? '').toUpperCase();
  if (success === 'Y') return;

  asArray(govt.ErrorDetails ?? govt.errorDetails).forEach(d => pushDetail(errors, d));

  const singleMessage = govt.ErrorMessage ?? govt.errorMessage ?? govt.Remarks ?? govt.remarks;
  if (singleMessage != null && String(singleMessage).trim()) {
    errors.push(String(singleMessage));
  }
};

const parseInvoice = (invoice: unknown, errors: string[]) => {
  const inv = asRecord(invoice);
  if (!inv) return;

  collectErrorDetails(inv, errors);

  const govt = asRecord(inv.govt_response ?? inv.govtResponse);
  if (govt) {
    collectErrorDetails(govt, errors);
    parseGovtResponse(govt, errors);
  }

  const status = String(inv.document_status ?? inv.documentStatus ?? '').toUpperCase();
  if (status.includes('FAILED') || status.includes('ERROR') || status.includes('REJECT')) {
    const errMsg = inv.error_message ?? inv.errorMessage ?? inv.message;
    if (errMsg != null && String(errMsg).trim()) {
      errors.push(String(errMsg));
    }
  }

  asArray(inv.errors).forEach(e => pushDetail(errors, e));
};

/** Extract user-facing errors from a ClearTax generate / validate IRN API payload. */
export const parseClearTaxIrnErrors = (data: unknown): string[] => {
  const errors: string[] = [];
  const body = asRecord(data);
  if (!body) return errors;

  asArray(body.invoices).forEach(inv => parseInvoice(inv, errors));

  const dataMap = asRecord(body.data);
  if (dataMap) {
    const govt = asRecord(dataMap.govt_response ?? dataMap.govtResponse);
    if (govt) parseGovtResponse(govt, errors);
  }

  if (errors.length === 0 && body.message != null) {
    const status = body.status;
    const documentStatus = String(dataMap?.document_status ?? '').toUpperCase();
    const isFailureStatus = typeof status === 'number' && status >= 400;
    const isFailedDocument =
      documentStatus.includes('FAILED') || documentStatus.includes('ERROR') || documentStatus.includes('REJECT');
    if (isFailureStatus || isFailedDocument) {
      errors.push(String(body.message));
    }
  }

  return dedupeErrors(errors);
};

/**
 * Parse validation failures from ClearTax generate API with dryRun=true.
 * Errors may appear on the invoice root, in govt_response.ErrorDetails, or in error_response.
 */
export const parseClearTaxValidateErrors = (data: unknown): string[] => {
  const fromApi = parseApiExceptionErrors(data);
  if (fromApi.length > 0) return fromApi;

  const errors = parseClearTaxIrnErrors(data);
  const body = asRecord(data);
  if (!body) return dedupeErrors(errors);

  collectErrorDetails(body, errors);
  const dataMap = asRecord(body.data);
  if (dataMap) {
    collectErrorDetails(dataMap, errors);
    const govt = asRecord(dataMap.govt_response ?? dataMap.govtResponse);
    if (govt) {
      collectErrorDetails(govt, errors);
      parseGovtResponse(govt, errors);
    }
  }

  return dedupeErrors(errors);
};

export const isClearTaxValidateSuccessful = (data: unknown): boolean =>
  parseClearTaxValidateErrors(data).length === 0;

export const extractIrnFromResponse = (data: unknown): string | null => {
  const body = asRecord(data);
  if (!body) return null;

  const dataMap = asRecord(body.data);
  const fromData = dataMap?.irn ?? dataMap?.Irn;
  if (fromData != null && String(fromData).trim()) return String(fromData).trim();

  for (const inv of asArray(body.invoices)) {
    const govt = asRecord(asRecord(inv)?.govt_response ?? asRecord(inv)?.govtResponse);
    const irn = govt?.Irn ?? govt?.irn;
    if (irn != null && String(irn).trim()) return String(irn).trim();
  }
  return null;
};

/** Unified parser for validate / generate IRN / cancel IRN / e-Invoice PDF errors. */
export const parseClearTaxErrors = (data: unknown): string[] => {
  const fromApi = parseApiExceptionErrors(data);
  if (fromApi.length > 0) return fromApi;

  const fromIrn = parseClearTaxIrnErrors(data);
  if (fromIrn.length > 0) return fromIrn;

  const body = asRecord(data);
  if (!body) return [];

  const errors: string[] = [];
  const clearTaxMessage = body.error_message ?? body.errorMessage;
  if (clearTaxMessage != null && String(clearTaxMessage).trim()) {
    errors.push(String(clearTaxMessage).trim());
  }

  if (body.message != null) {
    const status = body.status;
    if (typeof status === 'number' && status >= 400) {
      errors.push(String(body.message));
    }
  }

  return dedupeErrors(errors);
};

/** Extract errors from axios / API error responses. */
export const parseAxiosClearTaxError = (error: unknown): string[] => {
  const err = error as { response?: { data?: unknown }; message?: string };
  const data = err?.response?.data;

  const fromBody = parseClearTaxErrors(data);
  if (fromBody.length > 0) return fromBody;

  const dataRecord = asRecord(data);
  const lines: string[] = [];
  const description = dataRecord?.error_description ?? dataRecord?.message;
  if (description != null && String(description).trim()) {
    const text = String(description).trim();
    if (text.includes(';')) {
      return dedupeErrors(
        text
          .split(';')
          .map(part => formatClearTaxErrorMessage(part.trim()))
          .filter(Boolean),
      );
    }
    lines.push(text);
  } else if (err?.message) {
    lines.push(err.message);
  } else {
    lines.push('Something went wrong');
  }
  return dedupeErrors(lines);
};
